import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { Customer, Tour } from './Projects';
import { dbDeleteCustomer, dbDeleteTour, signOutUser, updateCustomer, updateFavoriteTour, updateOrderNumbers, updateTour } from '../firebase';
import { useNavigate } from 'react-router-dom';
import TourCard from '../components/TourCard';
import CustomerCard from '../components/CustomerCard';
import CreateEditTourDialog from '../components/CreateEditTourDialog';
import CreateEditCustomerDialog from '../components/CreateEditCustomerDialog';
import useConfirm from '../hooks/useConfirm';
import adminTheme from '../theme';

interface Props {
  tours: Tour[];
  customers: Customer[];
}
export default function Admin(props: Props) {

  const navigate = useNavigate();

  const logoutHandler = () => {
    signOutUser().then(() => navigate('/login'));
  }

  const [activeTab, setActiveTab] = useState(0);

  const [tours, setTours] = useState<Tour[]>([])
  const [editingTour, setEditingTour] = useState<Tour>();
  const [showDialog, setShowDialog] = useState(false);

  const [customers, setCustomers] = useState<Customer[]>([])
  const [editingCustomer, setEditingCustomer] = useState<Customer>();
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);

  const [Dialog, confirm] = useConfirm('Er du sikker?', 'Er du sikker på at du vil slette denne tour?');
  const [CustomerDialog, confirmCustomerDelete] = useConfirm('Er du sikker?', 'Er du sikker på at du vil slette denne kunde?');

  useEffect(() => {
    setTours(props.tours.sort((a, b) => a.order - b.order));
  }, [props.tours])

  useEffect(() => {
    setCustomers(props.customers);
  }, [props.customers])

  const updateTourHandler = (tour: Tour) => {
    if (!tour.id) return;
    updateTour(tour.id, tour).then(() => {
      setTours(prevTours => prevTours.map(_tour => _tour.id === tour.id ? tour : _tour))
      setOpenHandler(false);
    })
  }

  const deleteTourHandler = async (id: string) => {
    if (!id) return;

    const confirmed = await confirm();

    if (confirmed) {
      dbDeleteTour(id).then(() => {
        if (!id) return;
        setTours(prevTours => prevTours.filter(_tour => _tour.id !== id))
      })
    }
  }

  const setEditingTourHandler = (tour: Tour) => {
    setEditingTour(tour)
    setShowDialog(true)
  }

  const openCreateTourDialogHandler = () => {
    setEditingTour(undefined)
    setShowDialog(true)
  }

  const setOpenHandler = (open: boolean) => {
    if (!open) {
      setEditingTour(undefined)
    }
    setShowDialog(open);
  }

  const addTourHandler = (tour: Tour) => {
    setTours(prevTours => [...prevTours, tour]);
    setOpenHandler(false);
  }

  const swapElements = (index1: number, index2: number) => {
    if (!tours || index1 === tours.length || index2 === tours.length || index1 === -1 || index2 === -1) return;
    const array = [...tours];
    const temp = array[index1];
    array[index1] = array[index2];
    array[index2] = temp;

    const updatedTours = array.map((tour, index) => {
      return {
        ...tour,
        order: index + 1
      }
    })

    updateOrderNumbers(updatedTours).then(() => {
      setTours(updatedTours);
    });
  };

  const sortedTours = useMemo(() => {
    return tours.sort((a, b) => a.order - b.order)
  }, [tours])

  const setFavoriteTourHandler = (id: string) => {
    const updatedTours = tours.map(tour => {
      if (tour.id === id) {
        return {
          ...tour,
          showOnFrontpage: true
        }
      } else {
        return {
          ...tour,
          showOnFrontpage: false
        }
      }
    })

    updateFavoriteTour(updatedTours).then(() => {
      setTours(updatedTours);
    })
  }

  const setEditingCustomerHandler = (customer: Customer) => {
    setEditingCustomer(customer)
    setShowCustomerDialog(true)
  }

  const openCreateCustomerDialogHandler = () => {
    setEditingCustomer(undefined)
    setShowCustomerDialog(true)
  }

  const setCustomerDialogOpenHandler = (open: boolean) => {
    if (!open) {
      setEditingCustomer(undefined)
    }
    setShowCustomerDialog(open);
  }

  const addCustomerHandler = (customer: Customer) => {
    setCustomers(prevCustomers => [...prevCustomers, customer]);
    setCustomerDialogOpenHandler(false);
  }

  const updateCustomerHandler = (customer: Customer) => {
    if (!customer.id) return;
    updateCustomer(customer.id, customer).then(() => {
      setCustomers(prevCustomers => prevCustomers.map(_customer => _customer.id === customer.id ? customer : _customer))
      setCustomerDialogOpenHandler(false);
    })
  }

  const deleteCustomerHandler = async (id: string) => {
    if (!id) return;

    const confirmed = await confirmCustomerDelete();

    if (confirmed) {
      dbDeleteCustomer(id).then(() => {
        if (!id) return;
        setCustomers(prevCustomers => prevCustomers.filter(_customer => _customer.id !== id))
      })
    }
  }

  return (
    <ThemeProvider theme={adminTheme}>
      <>
      <CssBaseline />
      {Dialog()}
      {CustomerDialog()}

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Box sx={{ bgcolor: '#1F2430', color: 'white', py: { xs: 3, sm: 4 } }}>
          <Container maxWidth="lg">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h5" fontWeight={700}>VR Estate Admin</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Administrer tours og kunder</Typography>
              </Box>
              <Button variant="outlined" color="inherit" onClick={logoutHandler} sx={{ borderColor: 'rgba(255,255,255,0.5)' }}>
                Log ud
              </Button>
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              sx={{
                minHeight: 40,
                bgcolor: 'white',
                borderRadius: 3,
                p: 0.5,
                width: 'fit-content',
                border: '1px solid #ECEEF2',
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': { borderRadius: 2.5, minHeight: 36 },
                '& .Mui-selected': { bgcolor: '#4F46E5', color: 'white !important' },
              }}
            >
              <Tab label="Tours" />
              <Tab label="Kunder" />
            </Tabs>

            {activeTab === 0 ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateTourDialogHandler}>
                Ny tour
              </Button>
            ) : (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateCustomerDialogHandler}>
                Ny kunde
              </Button>
            )}
          </Stack>

          {activeTab === 0 && (
            <>
              <CreateEditTourDialog tours={tours} customers={customers} addTourCallback={addTourHandler} updateTourCallback={updateTourHandler} open={showDialog} setOpen={(open) => setOpenHandler(open)} editingTour={editingTour} />
              {sortedTours.length === 0 ? (
                <Typography color="text.secondary">Ingen tours endnu.</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2.5 }}>
                  {sortedTours.map((row, index) => (
                    <TourCard customers={customers} setFavoriteTour={setFavoriteTourHandler} swapElements={swapElements} index={index} key={row.id} onEditTourHandler={setEditingTourHandler} tour={row} deleteTour={(id) => deleteTourHandler(id)} />
                  ))}
                </Box>
              )}
            </>
          )}

          {activeTab === 1 && (
            <>
              <CreateEditCustomerDialog customers={customers} addCustomerCallback={addCustomerHandler} updateCustomerCallback={updateCustomerHandler} open={showCustomerDialog} setOpen={(open) => setCustomerDialogOpenHandler(open)} editingCustomer={editingCustomer} />
              {customers.length === 0 ? (
                <Typography color="text.secondary">Ingen kunder endnu.</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                  {customers.map((row) => (
                    <CustomerCard key={row.id} customer={row} onEditCustomerHandler={setEditingCustomerHandler} deleteCustomer={(id) => deleteCustomerHandler(id)} />
                  ))}
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>
      </>
    </ThemeProvider>
  );
}
